import React, { createContext, useContext, useMemo, useState } from 'react';
import en from './en/common.js';
import pt from './pt/common.js';

export type AppLanguage = 'en' | 'pt';

const STORAGE_KEY = 'tableArrangement_language';

const messagesByLanguage: Record<AppLanguage, Record<string, unknown>> = {
  en,
  pt,
};

interface I18nContextValue {
  language: AppLanguage;
  setLanguage: (language: AppLanguage) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

function getInitialLanguage(): AppLanguage {
  if (typeof window === 'undefined') {
    return 'en';
  }
  const saved = window.localStorage.getItem(STORAGE_KEY);
  return saved === 'pt' ? 'pt' : 'en';
}

function resolveMessage(source: Record<string, unknown>, key: string): unknown {
  return key.split('.').reduce<unknown>((current, part) => {
    if (!current || typeof current !== 'object' || !(part in (current as Record<string, unknown>))) {
      return undefined;
    }
    return (current as Record<string, unknown>)[part];
  }, source);
}

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) {
    return template;
  }
  return template.replace(/\{(\w+)\}/g, (_match, key) => String(vars[key] ?? `{${key}}`));
}

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<AppLanguage>(getInitialLanguage);

  const value = useMemo<I18nContextValue>(() => ({
    language,
    setLanguage: (nextLanguage: AppLanguage) => {
      setLanguageState(nextLanguage);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, nextLanguage);
      }
    },
    t: (key: string, vars?: Record<string, string | number>) => {
      const currentMessages = messagesByLanguage[language];
      const fallbackMessages = messagesByLanguage.en;
      const resolved = resolveMessage(currentMessages, key) ?? resolveMessage(fallbackMessages, key);
      return typeof resolved === 'string' ? interpolate(resolved, vars) : key;
    },
  }), [language]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
}
